import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Modal, TextInput, TouchableOpacity, ScrollView, KeyboardAvoidingView, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../context/ThemeContext';
import { useToast } from './Toast';
import { api } from '../api/client';
import Button from './Button';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';
import { useModalBottomPadding } from '../utils/useModalBottomPadding';

interface ReportProblemModalProps {
  readonly visible: boolean;
  readonly onClose: () => void;
  readonly transactionId: string;
  readonly locationName?: string;
  readonly bayName?: string;
  readonly amount?: number;
  readonly date?: string;
  readonly type?: string;
  readonly tierName?: string;
  readonly durationMinutes?: number;
  /** Fired after a report is successfully submitted (e.g. to mark the row reported). */
  readonly onSuccess?: () => void;
}

const CATEGORIES = [
  { key: 'wash_didnt_start', label: "Wash didn't start" },
  { key: 'wash_didnt_finish', label: "Wash didn't finish" },
  { key: 'wrong_amount', label: 'Wrong amount' },
  { key: 'equipment_malfunction', label: 'Equipment issue' },
  { key: 'water_soap_issue', label: 'Water/soap issue' },
  { key: 'other', label: 'Other' },
] as const;

// Vending purchases have their own failure modes — no wash/water categories.
const VENDING_CATEGORIES = [
  { key: 'vend_failure', label: "Item didn't vend" },
  { key: 'wrong_amount', label: 'Wrong amount' },
  { key: 'equipment_malfunction', label: 'Equipment issue' },
  { key: 'other', label: 'Other' },
] as const;

export default function ReportProblemModal({ visible, onClose, transactionId, locationName, bayName, amount, date, type, tierName, durationMinutes, onSuccess }: ReportProblemModalProps) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const modalBottomPadding = useModalBottomPadding();

  const [category, setCategory] = useState<string>('');
  const [description, setDescription] = useState('');
  const [contactEmail, setContactEmail] = useState(true);
  const [contactPhone, setContactPhone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userPhone, setUserPhone] = useState<string | null>(null);

  // Fetch user profile for contact info
  useEffect(() => {
    if (!visible) return;
    api<{ email: string; phone?: string }>('/api/auth/profile')
      .then(p => {
        setUserEmail(p.email);
        setUserPhone(p.phone || null);
      })
      .catch(() => {});
  }, [visible]);

  // Reset on open
  useEffect(() => {
    if (visible) {
      setCategory('');
      setDescription('');
      setContactEmail(true);
      setContactPhone(false);
      setSubmitting(false);
    }
  }, [visible]);

  const contactPreference = contactEmail && contactPhone ? 'both' : contactPhone ? 'phone' : 'email';
  const canSubmit = category && description.trim() && (contactEmail || contactPhone);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api('/api/reports', {
        method: 'POST',
        body: { transactionId, category, description, contactPreference },
      });
      toast.show({ type: 'success', title: 'Issue successfully reported', message: "We've notified the business." });
      onSuccess?.();
      onClose();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal visible={visible} animationType="slide" transparent>
      <KeyboardAvoidingView style={styles.overlay} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <View style={[styles.sheet, { paddingBottom: spacing.lg + modalBottomPadding }]}>
          <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
            {/* Header */}
            <View style={styles.header}>
              <Ionicons name="flag-outline" size={24} color={colors.danger} />
              <Text style={styles.title}>Report a Problem</Text>
              <TouchableOpacity onPress={onClose} hitSlop={8}>
                <Ionicons name="close" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            {/* Transaction summary */}
            {(locationName || bayName || amount != null) && (
              <View style={styles.txSummary}>
                {locationName && <Text style={styles.txText}>{locationName}{bayName ? ` — ${bayName}` : ''}</Text>}
                <View style={{ flexDirection: 'row', gap: spacing.md }}>
                  {amount != null && <Text style={styles.txText}>${(amount / 100).toFixed(2)}</Text>}
                  {type && <Text style={styles.txTextLight}>{type}{tierName ? ` — ${tierName}` : ''}</Text>}
                </View>
                {date && <Text style={styles.txTextLight}>{date}</Text>}
              </View>
            )}

            {/* Category */}
            <Text style={styles.label}>What went wrong?</Text>
            <View style={styles.chipRow}>
              {(type === 'vending' ? VENDING_CATEGORIES : CATEGORIES).map(c => (
                <TouchableOpacity
                  key={c.key}
                  style={[styles.chip, category === c.key && styles.chipActive]}
                  onPress={() => setCategory(c.key)}
                >
                  <Text style={[styles.chipText, category === c.key && styles.chipTextActive]}>{c.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Description */}
            <Text style={styles.label}>Description</Text>
            <TextInput
              style={styles.textArea}
              placeholder="Tell us what happened..."
              placeholderTextColor={colors.textLight}
              value={description}
              onChangeText={setDescription}
              multiline
              numberOfLines={4}
              maxLength={2000}
            />
            <Text style={styles.charCount}>{description.length}/2000</Text>

            {/* Contact preference */}
            <Text style={styles.label}>How should they contact you?</Text>
            <TouchableOpacity style={styles.checkRow} onPress={() => setContactEmail(!contactEmail)}>
              <View style={[styles.checkbox, contactEmail && styles.checkboxChecked]}>
                {contactEmail && <Ionicons name="checkmark" size={14} color={colors.white} />}
              </View>
              <Text style={styles.checkLabel}>Email</Text>
              <Text style={styles.checkValue}>{userEmail}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.checkRow, !userPhone && { opacity: 0.5 }]}
              onPress={() => userPhone && setContactPhone(!contactPhone)}
              disabled={!userPhone}
            >
              <View style={[styles.checkbox, contactPhone && styles.checkboxChecked]}>
                {contactPhone && <Ionicons name="checkmark" size={14} color={colors.white} />}
              </View>
              <Text style={styles.checkLabel}>Phone</Text>
              <Text style={styles.checkValue}>{userPhone || 'No phone on file'}</Text>
            </TouchableOpacity>

            {/* Submit */}
            <Button
              title="Submit Report"
              onPress={handleSubmit}
              loading={submitting}
              disabled={!canSubmit}
              size="lg"
              style={{ marginTop: spacing.lg }}
            />
            <Button
              title="Cancel"
              onPress={onClose}
              variant="ghost"
              style={{ marginTop: spacing.sm }}
            />
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  sheet: {
    backgroundColor: c.background, borderTopLeftRadius: 20, borderTopRightRadius: 20,
    padding: spacing.lg, maxHeight: '90%',
  },
  header: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.lg },
  title: { flex: 1, fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  txSummary: {
    backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.md, padding: spacing.md,
    marginBottom: spacing.lg, gap: 4,
  },
  txText: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  txTextLight: { fontSize: fontSize.xs, color: c.textSecondary },
  label: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.sm },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.lg },
  chip: {
    paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20,
    backgroundColor: c.surfaceSecondary, borderWidth: 1, borderColor: c.border,
  },
  chipActive: { backgroundColor: c.primary, borderColor: c.primary },
  chipText: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.medium },
  chipTextActive: { color: c.white },
  textArea: {
    backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, padding: spacing.md,
    fontSize: fontSize.sm, color: c.text, borderWidth: 1, borderColor: c.border,
    minHeight: 100, textAlignVertical: 'top',
  },
  charCount: { fontSize: fontSize.xs, color: c.textLight, textAlign: 'right', marginTop: 4, marginBottom: spacing.lg },
  checkRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm },
  checkbox: {
    width: 22, height: 22, borderRadius: 4, borderWidth: 2, borderColor: c.border,
    alignItems: 'center', justifyContent: 'center',
  },
  checkboxChecked: { backgroundColor: c.primary, borderColor: c.primary },
  checkLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text, width: 50 },
  checkValue: { fontSize: fontSize.xs, color: c.textSecondary, flex: 1 },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
